import type { LucideIcon } from 'lucide-react';
import type { NavGates } from '@/config/navVisibility';
import {
  BadgeDollarSign,
  Banknote,
  Building2,
  ClipboardList,
  Clock,
  FileText,
  FilePlus2,
  FolderOpen,
  Leaf,
  MapPin,
  Mic,
  NotebookPen,
  Package,
  PiggyBank,
  Receipt,
  User,
} from 'lucide-react';

export type QuickAddColumn = 'accounting' | 'resources' | 'other';

export type QuickAddDialogAction =
  | 'expense'
  | 'bill'
  | 'invoice'
  | 'journalEntry'
  | 'account'
  | 'project'
  | 'asset'
  | 'person'
  | 'business'
  | 'location'
  | 'file'
  | 'memo'
  | 'timesheet'
  | 'activity'
  | 'budget'
  | 'survey';

/** Gates are evaluated by the shared `isNavItemVisible` (QuickAddPanel). */
export interface QuickAddDialogItem extends NavGates {
  id: QuickAddDialogAction;
  labelKey: string;
  icon: LucideIcon;
  column: QuickAddColumn;
  kind: 'dialog';
}

export interface QuickAddNavigateItem extends NavGates {
  id: string;
  labelKey: string;
  icon: LucideIcon;
  column: QuickAddColumn;
  kind: 'navigate';
  href: string;
}

export type QuickAddItem = QuickAddDialogItem | QuickAddNavigateItem;

export const quickAddItems: QuickAddItem[] = [
  // Accounting
  { id: 'expense', labelKey: 'nav.quickAdd.items.expense', icon: BadgeDollarSign, column: 'accounting', kind: 'dialog', requiresScope: 'accounting' },
  { id: 'bill', labelKey: 'nav.quickAdd.items.bill', icon: Receipt, column: 'accounting', kind: 'dialog', requiresScope: 'accounting' },
  { id: 'invoice', labelKey: 'nav.quickAdd.items.invoice', icon: FileText, column: 'accounting', kind: 'dialog', requiresScope: 'accounting' },
  { id: 'journalEntry', labelKey: 'nav.quickAdd.items.journalEntry', icon: NotebookPen, column: 'accounting', kind: 'dialog', requiresScope: 'accounting' },
  { id: 'account', labelKey: 'nav.quickAdd.items.account', icon: Banknote, column: 'accounting', kind: 'dialog', requiresScope: 'accounting' },

  // Resources
  { id: 'project', labelKey: 'nav.quickAdd.items.project', icon: FolderOpen, column: 'resources', kind: 'dialog' },
  { id: 'asset', labelKey: 'nav.quickAdd.items.asset', icon: Package, column: 'resources', kind: 'dialog' },
  { id: 'person', labelKey: 'nav.quickAdd.items.person', icon: User, column: 'resources', kind: 'dialog' },
  { id: 'business', labelKey: 'nav.quickAdd.items.business', icon: Building2, column: 'resources', kind: 'dialog' },
  // Phase 5: the quick-add creates a PLACE (the go-forward model) — the old
  // address-only dialog call site was broken (W10). Id stays 'location' (it
  // is only a dialog switch key, not persisted).
  { id: 'location', labelKey: 'nav.quickAdd.items.place', icon: MapPin, column: 'resources', kind: 'dialog' },
  { id: 'file', labelKey: 'nav.quickAdd.items.file', icon: FilePlus2, column: 'resources', kind: 'dialog' },
  { id: 'survey', labelKey: 'nav.quickAdd.items.survey', icon: ClipboardList, column: 'resources', kind: 'dialog', featureFlag: 'surveys', requiresScope: 'surveys' },

  // Other
  { id: 'memo', labelKey: 'nav.quickAdd.items.memo', icon: Mic, column: 'other', kind: 'dialog' },
  { id: 'timesheet', labelKey: 'nav.quickAdd.items.timesheet', icon: Clock, column: 'other', kind: 'dialog' },
  { id: 'activity', labelKey: 'nav.quickAdd.items.activity', icon: Leaf, column: 'other', kind: 'dialog' },
  { id: 'budget', labelKey: 'nav.quickAdd.items.budget', icon: PiggyBank, column: 'other', kind: 'dialog', requiresScope: 'accounting' },
];

export const quickAddColumns: { id: QuickAddColumn; labelKey: string }[] = [
  { id: 'accounting', labelKey: 'nav.quickAdd.columns.accounting' },
  { id: 'resources', labelKey: 'nav.quickAdd.columns.resources' },
  { id: 'other', labelKey: 'nav.quickAdd.columns.other' },
];
